import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import useRootStore from '@/stores/useRootStore';

const LeaveOrganization = graphql(/* GraphQL */ `
  mutation LeaveOrganization($organizationId: uuid!, $personId: String!) {
    delete_event_person_by_pk(event_id: $organizationId, person_id: $personId) {
      event_id
      person_id
    }
  }
`);

const GetOrganizationLeave = graphql(/* GraphQL */ `
  query GetOrganizationLeave($organizationId: uuid!) {
    event_by_pk(id: $organizationId) {
      id
      title
      event_type
    }
  }
`);

export default function OrganizationLeave() {
  const personId = useUserId();
  const { organizationId } = useLocalSearchParams<{
    organizationId: string;
  }>();
  const { dismissTo } = useRouter();
  const [, leaveOrganization] = useMutation(LeaveOrganization);

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetOrganizationLeave,
    variables: { organizationId },
  });

  const organization = data?.event_by_pk;

  const onPressLeave = async () => {
    const { data: mutationData, error } = await leaveOrganization({
      organizationId,
      personId,
    });

    if (error || !mutationData?.delete_event_person_by_pk) {
      return { success: false };
    }
    return { success: true };
  };

  const onSuccess = () => {
    useRootStore.setState({
      activeContextPathname: null,
      activeTab: 'event',
      organizationId: null,
    });
    dismissTo('/event');
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: organization?.title ? `Leave ${organization.title}` : 'Leave',
        }}
      />
      <ContentBoundary
        data={
          organization?.event_type === 'organization' && organization.title
            ? { id: organization.id, title: organization.title }
            : null
        }
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Leaving is unavailable." />}
      >
        {(organization) => (
          <DestructiveAction
            key={organization.id}
            label="Leave organization"
            submittingLabel="Leaving…"
            onAction={onPressLeave}
            onSuccess={onSuccess}
            testID="OrganizationLeaveButton"
          >
            <DestructiveActionHeading>
              Are you sure you want to leave {organization.title}?
            </DestructiveActionHeading>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
